import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Select } from 'syngenta-digital-cropwise-react-ui-kit';
import usePeopleData from 'hooks/usePeopleData';
import useCompanies from 'hooks/useCompanies';
import { useDataRefresh } from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/hooks/useDataRefresh';
import { CompanyPeopleItem } from 'components/CompanyPeopleDropdown/types';
import AddNewCompanyPeopleDrawer from 'components/CompanyPeopleDropdown/components/AddNewCompanyPeopleDrawer';
import getDropdownParentWithoutDetaching from 'utilities/getDropdownParentWithoutDetaching';
import {
  FormValues,
  PersonCompany
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import CreateNewButton from 'components/CreateNewButton';
import filterOptionByNormalizedString from 'screens/Applications&WorkOrders/StepsLayout/Helpers/filterOptions';
import focusInput from 'screens/Applications&WorkOrders/StepsLayout/Helpers/focusInput';
import PersonOptionLabel from 'screens/Applications&WorkOrders/StepsLayout/Components/DetailsStep/components/PersonOptionLabel';

interface AuthorizedBySelectProps {
  value?: PersonCompany;
  onChange: (value: PersonCompany | null) => void;
  onBlur: () => void;
  onAddNewClick?: () => void;
  style?: React.CSSProperties;
  listHeight?: number;
  suffixIcon?: React.ReactNode;
  onDropdownVisibleChange?: (open: boolean) => void;
}

const AuthorizedBySelect: React.FC<AuthorizedBySelectProps> = ({
  value,
  onChange,
  onBlur,
  onAddNewClick,
  style,
  listHeight,
  suffixIcon,
  onDropdownVisibleChange
}) => {
  const [isCreateNewOpen, setIsCreateNewOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { people, loading: peopleLoading, loadPeople } = usePeopleData();
  const {
    companies,
    loading: companiesLoading,
    loadCompanies
  } = useCompanies();
  const { values } = useFormikContext<FormValues>();

  const { subscribe, triggerRefresh } = useDataRefresh();

  useEffect(() => {
    const unsubscribe = subscribe(() => {
      loadPeople();
      loadCompanies();
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  // Helper function to get display value for the Select
  const getDisplayValue = useMemo(() => {
    if (!value?.id) return undefined;

    const existsInPeople = people?.some(
      (p: { id: string }) => p.id === value.id
    );
    const existsInCompanies = companies?.some(
      (c: { id: string }) => c.id === value.id
    );

    if (existsInPeople || existsInCompanies) {
      return value.id;
    }

    return value.name ? `temp_${value.id}` : value.id;
  }, [value, people, companies]);

  const handleChange = (selectedValue: string, option: any) => {
    if (selectedValue && option) {
      const selectedItem = option.item;
      onChange(selectedItem);
    } else {
      onChange(null);
    }
  };

  const handleClear = () => onChange(null);

  const handleCreateNew = () => {
    setIsCreateNewOpen(true);
    if (onDropdownVisibleChange) {
      onDropdownVisibleChange(false);
    }
    if (onAddNewClick) {
      onAddNewClick();
    }
  };

  const refocusParentInput = () => {
    focusInput(containerRef, 'input');
  };

  const handleCompanyPeopleAdded = (newValue: CompanyPeopleItem | null) => {
    if (newValue) {
      onChange({
        id: newValue.id,
        name: newValue.name,
        type: newValue.type,
        licenseNumber: newValue.licenseNumber
      });

      if (newValue.type === 'People') {
        loadPeople();
        triggerRefresh('create-application-people');
      } else {
        loadCompanies();
        triggerRefresh('create-application-companies');
      }
    }
    setIsCreateNewOpen(false);
    refocusParentInput();
  };

  const allowClear = !!values.authorizedBy?.id;

  const hasTempOption = useMemo(() => {
    return (
      value?.id &&
      value?.name &&
      !people?.some((p: { id: string }) => p.id === value.id) &&
      !companies?.some((c: { id: string }) => c.id === value.id)
    );
  }, [value, people, companies]);

  const peopleOptions = useMemo(() => {
    return Array.isArray(people)
      ? people.map((person: PersonCompany) => ({
          value: person.id,
          item: {
            id: person.id,
            name: person.name,
            type: 'People',
            licenseNumber: person.licenseNumber
          },
          children: person.name
        }))
      : [];
  }, [people]);

  const companyOptions = useMemo(() => {
    return Array.isArray(companies)
      ? companies.map((company: PersonCompany) => ({
          value: company.id,
          item: {
            id: company.id,
            name: company.name,
            type: 'Company'
          },
          children: company.name
        }))
      : [];
  }, [companies]);

  const dropdownRender = (menu: React.ReactElement) => (
    <div className="cwf-custom-select-dropdown">
      {menu}
      <CreateNewButton onCreateNew={handleCreateNew} />
    </div>
  );

  return (
    <div ref={containerRef}>
      <Select
        data-testid="authorized-by-select"
        style={style}
        showSearch
        loading={peopleLoading || companiesLoading}
        value={getDisplayValue}
        optionLabelProp="label"
        onChange={handleChange}
        onBlur={onBlur}
        allowClear={allowClear}
        onClear={handleClear}
        className="custom-select cwf-select-authorized-by"
        listHeight={listHeight}
        showArrow
        dropdownStyle={{ whiteSpace: 'normal' }}
        suffixIcon={suffixIcon}
        onDropdownVisibleChange={onDropdownVisibleChange}
        getPopupContainer={getDropdownParentWithoutDetaching}
        dropdownRender={dropdownRender}
        filterOption={filterOptionByNormalizedString}
      >
        {/* // temporary option for newly created items while refetch in background */}
        {hasTempOption && (
          <Select.Option
            key={`temp_${value!.id}`}
            value={`temp_${value!.id}`}
            item={value}
            label={value!.name}
            style={{ display: 'none' }}
          >
            {value!.name}
          </Select.Option>
        )}

        {peopleOptions.length > 0 && (
          <Select.OptGroup label="PEOPLE">
            {peopleOptions.map(
              (option: {
                value: string;
                item: PersonCompany;
                children: string;
              }) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  item={option.item}
                  label={option.item.name}
                  data-testid={`authorized-people-by-${option.children}-option`}
                >
                  <PersonOptionLabel
                    name={option.item.name}
                    licenseNumber={option.item.licenseNumber}
                  />
                </Select.Option>
              )
            )}
          </Select.OptGroup>
        )}

        {companyOptions.length > 0 && (
          <Select.OptGroup label="COMPANIES">
            {companyOptions.map(
              (option: {
                value: string;
                item: PersonCompany;
                children: string;
              }) => (
                <Select.Option
                  key={option.value}
                  value={option.value}
                  item={option.item}
                  label={option.children}
                >
                  {option.children}
                </Select.Option>
              )
            )}
          </Select.OptGroup>
        )}
      </Select>

      <AddNewCompanyPeopleDrawer
        defaultType="People"
        title="Create New"
        open={isCreateNewOpen}
        onClose={() => {
          setIsCreateNewOpen(false);
          refocusParentInput();
        }}
        onCompanyPeopleAdded={handleCompanyPeopleAdded}
      />
    </div>
  );
};

export default AuthorizedBySelect;
